import React from 'react';
import Icon, { IconName } from './icons/Icon';
import './Button.css';

interface ButtonProps {
  // Text content
  children: React.ReactNode;
  
  // Icon configuration
  leftIcon?: IconName;
  rightIcon?: IconName;
  
  // Button behavior
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  done?: boolean;
  
  // Styling
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  
  // HTML attributes
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  'data-testid'?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  leftIcon,
  rightIcon,
  onClick,
  disabled = false,
  loading = false,
  done = false,
  variant = 'primary',
  size = 'medium',
  className = '',
  type = 'button',
  id,
  'data-testid': dataTestId,
}) => {
  // Determine the current state for CSS classes
  const getStateClasses = () => {
    const classes = ['button'];
    
    classes.push(`button--${variant}`);
    classes.push(`button--${size}`);
    
    if (disabled) classes.push('button--disabled');
    if (loading) classes.push('button--loading');
    if (done) classes.push('button--done');
    
    return classes.join(' ');
  };

  // Handle click events
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      event.preventDefault();
      return;
    }
    
    if (onClick) {
      onClick(event);
    }
  };

  // Show loading spinner instead of left icon when loading
  const shouldShowLeftIcon = leftIcon && !loading;
  const shouldShowLoadingIcon = loading;
  const shouldShowRightIcon = rightIcon && !loading;
  
  // Icon size based on button size
  const getIconSize = () => {
    switch (size) {
      case 'small': return 20;
      case 'large': return 24;
      default: return 24; // medium
    }
  };
  
  const iconSize = getIconSize();

  const getTextClass = () => {
    switch (size) {
      case 'small':
        return 'text-single-medium'; // 14px
      case 'large':
        return 'text-single-xlarge'; // 18px
      default:
        return 'text-single-large'; // 16px
    }
  };

  return (
    <button
      type={type}
      id={id}
      data-testid={dataTestId}
      className={`${getStateClasses()} ${className}`}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-disabled={disabled}
    >
      {/* Variable Layer - handles background styling */}
      <div className="button__variable">
        {/* State Layer - handles padding and interactive states */}
        <div className="button__state-layer">
          {/* Left Icon */}
          {shouldShowLeftIcon && (
            <div className="button__left-icon">
              <Icon name={leftIcon} size={iconSize} />
            </div>
          )}
          
          {/* Loading Icon */}
          {shouldShowLoadingIcon && (
            <div className="button__left-icon button__loading-icon">
              <Icon name="settings" size={iconSize} />
            </div>
          )}
          
          {/* Text Content */}
          <div className={`button__text ${getTextClass()}`}>
            {children}
          </div>
          
          {/* Right Icon */}
          {shouldShowRightIcon && (
            <div className="button__right-icon">
              <Icon name={rightIcon} size={iconSize} />
            </div>
          )}
          
          {/* Done Icon */}
          {done && (
            <div className="button__right-icon">
              <Icon name="check" size={iconSize} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

export default Button;
